import { useState, useEffect, useRef, useCallback } from 'react';
import { api, Spinner, fmtDateTime, fmtCurrency } from './SecretaryPortal';
import { useEmailComposer } from './EmailComposer';

// ─── Online entry submission decide dialog ────────────────────────────────────
//
// Opened by clicking a 'received'/'pending' status pill in SubmissionsList, or
// the "Accepted" status in EntryList (for Cancel/Request Refund). One
// .sec-modal for the review content (race-number history, ACU-hash/name-
// mismatch banners, fee + payment preview, email text), plus the existing
// lightweight .confirm-overlay/.confirm-card pattern (same one EntryForm uses
// for its duplicate-race-number warning) as the final irreversible-action
// gate — never a second full modal.
//
// received -> accepted/pending both create the real entry_list row(s); the
// dialog lets the secretary confirm/edit each machine's race number first.
// pending -> accepted only flips status (rows already exist from the hold
// step) — race numbers aren't editable here, use the existing entry-edit UI
// for that instead. That decision (race number, fee, ACU/name-mismatch
// checks) already happened once at hold-time, so a pending submission
// skips the /review fetch entirely and shows a lightweight summary — no
// point re-running the same computation just to compose an email.
//
// received/pending -> rejected and accepted -> cancelled are also handled
// here. Reject reuses the same email composer as accept (with different
// default text) since the entrant hasn't otherwise been told anything;
// cancel is silent (usually rider-initiated) and skips the composer
// entirely. Neither bundles a refund request — that's always a separate,
// standalone action (see RefundRequestForm in SecretaryPortal.jsx),
// independent of submission status.
//
// Two-step flow, one modal throughout (never two stacked windows):
//   1. Footer starts as a "Decide ▾" menu (or one plain button when there's
//      only one possible action, e.g. Cancel Entry) rather than several
//      buttons side by side. Picking an option sets `chosenAction`, which
//      swaps the footer's second button to that action's own label and
//      reveals its extra content in THIS SAME body (email composer for
//      accept/reject, plus a reason field for reject; nothing extra for
//      hold/cancel) — shown only once a choice has actually been made, not
//      always-visible up front. Changing your mind means Cancel (closes the
//      whole dialog) and reopening — there's no separate "back" step.
//   2. Clicking the action button in that footer opens the existing small
//      .confirm-card overlay as the final "this cannot be undone" gate —
//      unchanged, text-only, same as it's always been. Backing out of THAT
//      just closes the small card, returning to the one big modal (already
//      showing the composer), not a second modal to also dismiss.
// Opening via a status pill's own dropdown (see DecidePill in
// SecretaryPortal.jsx) auto-lands on step 1 already composing that action,
// skipping only the initial Decide-menu click.

const DEFAULT_EMAIL_SUBJECT = 'Your NHCA entry is confirmed';
const DEFAULT_REJECTION_SUBJECT = 'Your NHCA entry';

// Same coverage-code information the accept dialog itself shows (see the
// info-banner rendered for review.coverage below) — worth telling the
// entrant directly, since it's otherwise only visible to the secretary.
// Reflects whatever's true right now: the group may not be complete yet
// (e.g. the payer gets accepted before everyone they're covering has even
// submitted), so this never claims to be final, just a snapshot.
function coverageEmailParagraph(coverage) {
  if (!coverage) return '';
  if (coverage.role === 'payer') {
    const code = formatCoverageCode(coverage.coverage_code);
    if (!coverage.covered_entrants || coverage.covered_entrants.length === 0) {
      return `\n\nYour coverage code (${code}) hasn't been used by anyone yet — share it with anyone whose entry fee this payment also covers.`;
    }
    const names = coverage.covered_entrants.map(c => `${c.rider_name} (${c.status})`).join(', ');
    let line = `\n\nYour coverage code (${code}) has been used by: ${names}. Group total fee so far: ${fmtCurrency(coverage.group_total_fee)}.`;
    if (coverage.group_surplus > 0) line += ` There's currently a surplus of ${fmtCurrency(coverage.group_surplus)} on the group payment.`;
    if (coverage.group_total_shortfall > 0) line += ` There's currently a shortfall of ${fmtCurrency(coverage.group_total_shortfall)} still owed across the group.`;
    line += ` If anyone else still plans to use this code, these figures will change once they've entered.`;
    return line;
  }
  if (coverage.role === 'covered') {
    return `\n\nYour entry fee has been covered by ${coverage.payer_rider_name}'s bank transfer — no payment is needed from you.`;
  }
  return '';
}

// Plain text, not HTML — sent via send_email(text=...), so newlines are all
// the formatting available (no rich-text editor for a short acceptance email).
// Who the entrant should ask: the event's secretary, by name and address
// where the calendar has them.
function contactSecretaryLine(event, inTheMeantime = false) {
  const who = [event.secretary, event.secretary_email && `(${event.secretary_email})`]
    .filter(Boolean).join(' ');
  return `If you have any questions${inTheMeantime ? ' in the meantime' : ''}, ` +
    `please contact the event secretary${who ? `, ${who}` : ''}.`;
}

function defaultEmailBody(submission, event, raceNumbers, coverage) {
  const rider = submission.payload?.rider || {};
  const name = rider.firstName || 'there';
  const hill = event.Hill || event.eventname || 'the event';
  const numbers = raceNumbers.length ? raceNumbers.join(', ') : 'your entry';
  return (
    `Hi ${name},\n\n` +
    `Good news — your entry for ${hill} has been accepted ` +
    `(race number${raceNumbers.length > 1 ? 's' : ''}: ${numbers}).` +
    coverageEmailParagraph(coverage) +
    `\n\nFull event details and joining instructions will follow closer to the date. ` +
    `${contactSecretaryLine(event, true)}\n\n` +
    `See you there!`
  );
}

// Two different default bodies, since a 'pending' rejection follows a
// committee review the entrant may be expecting an answer to, while a
// 'received' rejection is immediate — the entrant hasn't heard anything yet.
function defaultRejectionEmailBody(submission, event, isPending, reason) {
  const rider = submission.payload?.rider || {};
  const name = rider.firstName || 'there';
  const hill = event.Hill || event.eventname || 'the event';
  const reasonLine = reason ? `\n\n${reason}` : '';
  return isPending
    ? (`Hi ${name},\n\n` +
       `Thank you for your patience while your entry for ${hill} was reviewed by the committee. ` +
       `Unfortunately, we're unable to offer you a place in this event.${reasonLine}\n\n` +
       contactSecretaryLine(event))
    : (`Hi ${name},\n\n` +
       `Thank you for your entry for ${hill}. Unfortunately we're unable to accept your entry ` +
       `for this event.${reasonLine}\n\n` +
       contactSecretaryLine(event));
}

function riderName(submission) {
  const r = submission.payload?.rider || {};
  return [r.firstName, r.surname].filter(Boolean).join(' ') || 'this rider';
}

function formatCoverageCode(code) {
  if (!code) return null;
  return `${code.slice(0, 4)}-${code.slice(4, 8)}-${code.slice(8)}`;
}

// Why a rider is suggested for a name that isn't in the rider list
// (riders.review in the backend).
const SUGGESTION_REASONS = {
  acu: 'same ACU number',
  race_number: 'has used this race number',
  surname: 'same surname',
};

// "13 Sept 2026": last_seen.date is a plain YYYY-MM-DD, so read it as UTC.
const fmtDay = iso => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

function riderHistory(r) {
  if (!r.events) return 'no earlier results or entries';
  const last = r.last_seen ? `, last at ${r.last_seen.hill} on ${fmtDay(r.last_seen.date)}` : '';
  return `${r.events} earlier event${r.events === 1 ? '' : 's'}${last}`;
}

// Who the entry belongs to. A known name needs nothing; a new one must be
// matched to an existing rider or added as new before Hold/Accept, so the
// rider list stays one row per person. The chosen rider's spelling goes on
// the entry; the signed submission keeps what the entrant typed.
function RiderSection({ riderReview, submission, choice, onChoose }) {
  if (!riderReview) return null;
  if (riderReview.status === 'known') {
    const r = riderReview.rider;
    // A rider with no earlier events can only have been added in the portal,
    // so say by whom rather than "no earlier results".
    const added = r.added ? `, added by ${r.added.by} on ${fmtDay(r.added.on)}` : '';
    return (
      <div className="info-banner" style={{ background: '#e8f5e9', borderColor: '#2e7d32', marginBottom: '1rem' }}>
        <strong>Known rider ✓</strong> {r.first_name} {r.last_name}{added}
        {r.events ? `, ${riderHistory(r)}` : ''}.
      </div>
    );
  }
  const typed = riderName(submission);
  return (
    <div className="info-banner" style={{ background: '#fff8e1', borderColor: '#f9a825', marginBottom: '1rem' }}>
      <p style={{ marginBottom: '0.5rem' }}>
        <strong>New name:</strong> <strong>{typed}</strong> is not known.
      </p>
      {riderReview.suggestions.map(r => (
        <label key={r.id} style={{ display: 'block', margin: '0.35rem 0', cursor: 'pointer' }}>
          <input
            type="radio"
            name="rider-choice"
            checked={choice?.id === r.id}
            onChange={() => onChoose(r)}
            style={{ marginRight: '0.5rem' }}
          />
          <strong>{r.first_name} {r.last_name}</strong> ({r.reasons.map(x => SUGGESTION_REASONS[x] || x).join(', ')})
          {' '}<span style={{ color: 'var(--text-light)' }}>{riderHistory(r)}</span>
        </label>
      ))}
      <label style={{ display: 'block', margin: '0.35rem 0', cursor: riderReview.can_add ? 'pointer' : 'default' }}>
        <input
          type="radio"
          name="rider-choice"
          checked={choice === 'new'}
          disabled={!riderReview.can_add}
          onChange={() => onChoose('new')}
          style={{ marginRight: '0.5rem' }}
        />
        Add <strong>{typed}</strong> to the rider list as a new rider
        {riderReview.can_add
          ? <span style={{ color: 'var(--text-light)' }}> (added when you hold or accept)</span>
          : ' (needs both a first and a last name: reject or contact the entrant)'}
      </label>
    </div>
  );
}

// Give It A Go is for first-timers, once per person. Shown when the rider (the
// known one, or the suggestion picked) has used GIAG before or has earlier
// results or entries. The secretary decides: it never blocks Hold/Accept.
function GiagWarning({ licence, riderReview, choice }) {
  if (licence?.option !== 'giag' || !riderReview) return null;
  const r = riderReview.status === 'known' ? riderReview.rider : (choice && choice !== 'new' ? choice : null);
  if (!r || !(r.giag_used || r.events)) return null;
  const name = `${r.first_name} ${r.last_name}`;
  return (
    <div className="info-banner" style={{ background: '#fdecea', borderColor: '#c62828', marginBottom: '1rem' }}>
      <strong>Give It A Go is for first-timers, once per person.</strong>{' '}
      {r.giag_used
        ? `${name} already entered with Give It A Go at ${r.giag_used.hill} on ${fmtDay(r.giag_used.date)}.`
        : `${name} has raced before: ${riderHistory(r)}.`}
      {' '}You can still accept, or reject the entry and ask them to enter at the normal fee with a day licence.
    </div>
  );
}

export default function SubmissionActionDialog({ submission, event, sessionToken, onClose, onActioned, initialAction }) {
  const [review, setReview]           = useState(null);
  const [reviewLoading, setReviewLoading] = useState(true);
  const [reviewError, setReviewError] = useState(null);

  const [assignments, setAssignments] = useState({}); // { machineIndex: raceNumberString }
  const [machineHistory, setMachineHistory] = useState({}); // { machineIndex: [] }
  const debounceRefs = useRef({});

  // Subject, message, saved templates and the PDF: shared with the
  // email-all-entrants screen (see EmailComposer.jsx).
  const { setEmailSubject, setEmailBody, emailPayload, renderComposer } =
    useEmailComposer(sessionToken, DEFAULT_EMAIL_SUBJECT);

  // chosenAction: the action currently being composed, still within this one
  // modal (drives the extra body content + footer). confirming: only set once
  // the footer's own action button is clicked — opens the small final gate.
  const [chosenAction, setChosenAction] = useState(null); // null | 'accept' | 'hold' | 'reject' | 'cancel'
  const [confirming, setConfirming]     = useState(null); // null | 'accept' | 'hold' | 'reject' | 'cancel'
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [conflict, setConflict]       = useState(null); // { machine_index, race_number }
  const [riderChoice, setRiderChoice] = useState(null); // null | rider from review.rider.suggestions | 'new'
  const [rejectReason, setRejectReason] = useState('');

  // Bundled refund request, offered alongside reject/cancel (still fired as
  // its own separate POST .../refund-request call after the main action
  // succeeds — refund tracking stays independent of submission status at
  // the API level, this is purely a same-modal UX convenience).
  const [wantsRefund, setWantsRefund] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundNote, setRefundNote] = useState('');

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const isReceived = submission.status === 'received';
  const isPending  = submission.status === 'pending';
  const isAccepted = submission.status === 'accepted';
  const machines = submission.payload?.machines || [];

  // A BACS-funded submission can't be accepted until the financial
  // secretary has confirmed the bank transfer — see bacs_confirmation on
  // the review payload (_bacs_confirmation_status, secretary_routes.py).
  // 'underpaid' is a warning, not a block — see the banner below.
  const bacsStatus = review?.bacs_confirmation?.status || null;
  const bacsBlocksAccept = bacsStatus === 'unconfirmed';

  // A new rider name must be matched or added before the entry rows exist.
  const riderUnsettled = isReceived && review?.rider?.status === 'new' && !riderChoice;
  const riderPayload = riderChoice === 'new' ? { new: true } : riderChoice ? { id: riderChoice.id } : undefined;

  // One menu item per action available for this submission's current
  // status; 'danger' just picks the red button style for a destructive one.
  const decideOptions = isAccepted
    ? [{ action: 'cancel', label: 'Cancel Entry', danger: true }]
    : isPending
      ? [
          { action: 'reject', label: 'Reject', danger: true },
          { action: 'accept', label: 'Accept & Notify', disabled: bacsBlocksAccept },
        ]
      : [
          { action: 'reject', label: 'Reject', danger: true },
          { action: 'hold', label: 'Hold for Review', disabled: riderUnsettled },
          { action: 'accept', label: 'Accept', disabled: bacsBlocksAccept || riderUnsettled },
        ];

  const actionLabel = {
    accept: isPending ? 'Accept & Notify' : 'Accept',
    hold: 'Hold for Review',
    reject: 'Reject',
    cancel: 'Cancel Entry',
  }[chosenAction];

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const load = useCallback(async () => {
    // Fetched for every status now, including pending/accepted (the backend
    // allows 'accepted' through specifically for this) — Cancel Entry's
    // "request a refund" default amount needs payment_preview.total_fee,
    // which correctly excludes what a coverage-group payer is covering for
    // other riders, or reflects a covered entrant's real fee despite their
    // own payment_amount being 0.00. For pending/accepted the race numbers
    // are the entry list's (entry_race_numbers): assigned at hold/accept
    // time, perhaps renumbered since with Edit, and not editable here.
    setReviewLoading(true);
    setReviewError(null);
    try {
      const data = await api(
        `/api/secretary/entries/${event.CalendarID}/submissions/${submission.id}/review`,
        {}, sessionToken
      );
      setReview(data);
      if (isPending || isAccepted) {
        const initialAssignments = {};
        machines.forEach((m, i) => {
          const n = data.entry_race_numbers?.[i] ?? m.ridingNo;
          initialAssignments[i] = n != null ? String(n) : '';
        });
        setAssignments(initialAssignments);
        if (isPending) {
          const raceNumbers = Object.values(initialAssignments).filter(Boolean);
          setEmailBody(defaultEmailBody(submission, event, raceNumbers, data.coverage));
        }
      } else {
        const initialAssignments = {};
        const initialHistory = {};
        data.machines.forEach(m => {
          initialAssignments[m.machine_index] = m.riding_no != null ? String(m.riding_no) : '';
          initialHistory[m.machine_index] = m.history || [];
        });
        setAssignments(initialAssignments);
        setMachineHistory(initialHistory);
        const raceNumbers = Object.values(initialAssignments).filter(Boolean);
        setEmailBody(defaultEmailBody(submission, event, raceNumbers, data.coverage));
      }
    } catch (err) {
      setReviewError(err.message);
    } finally {
      setReviewLoading(false);
    }
  }, [event.CalendarID, submission.id, sessionToken, isPending, isAccepted]);

  // Guarded with a ref, not just the effect's own deps: React StrictMode
  // double-invokes effects in dev, and load() has no cleanup to cancel the
  // first (stale) call — without this, both invocations' async work resolves
  // independently, and the second one's setEmailBody(accept-default) can
  // land AFTER pickAction's auto-jump already set the rejection body,
  // silently reverting just the body (emailSubject is only ever set by
  // pickAction, never touched by load(), so it stays correct — that
  // mismatch, subject right/body wrong, is what actually surfaced this).
  // The "Retry" button still calls load() directly, unaffected by this guard.
  const mountLoadRanRef = useRef(false);
  useEffect(() => {
    if (mountLoadRanRef.current) return;
    mountLoadRanRef.current = true;
    load();
  }, [load]);

  const setAssignment = (machineIndex, value) => {
    setAssignments(a => ({ ...a, [machineIndex]: value }));
    setConflict(null);
    clearTimeout(debounceRefs.current[machineIndex]);
    const num = parseInt(value, 10);
    if (!num) {
      setMachineHistory(h => ({ ...h, [machineIndex]: [] }));
      return;
    }
    debounceRefs.current[machineIndex] = setTimeout(async () => {
      try {
        const results = await api(`/api/secretary/search-number/${num}`, {}, sessionToken);
        setMachineHistory(h => ({ ...h, [machineIndex]: results }));
      } catch { /* ignore — history is informational only */ }
    }, 500);
  };

  const buildAssignmentsPayload = () =>
    Object.entries(assignments)
      .filter(([, v]) => v)
      .map(([machineIndex, raceNumber]) => ({ machine_index: Number(machineIndex), race_number: parseInt(raceNumber, 10) }));

  const doSubmit = async (action) => {
    setSubmitting(true);
    setSubmitError(null);
    setConflict(null);
    try {
      const path = `/api/secretary/entries/${event.CalendarID}/submissions/${submission.id}/${action}`;
      let body;
      if (action === 'hold') {
        body = { assignments: buildAssignmentsPayload(), rider: riderPayload };
      } else if (action === 'cancel') {
        body = {}; // cancel is silent — no email, no assignments to carry over
      } else if (action === 'reject') {
        body = { reason: rejectReason, email: emailPayload() };
      } else {
        body = {
          ...(isReceived ? { assignments: buildAssignmentsPayload(), rider: riderPayload } : {}),
          email: emailPayload(),
        };
      }
      await api(path, { method: 'POST', body: JSON.stringify(body) }, sessionToken);

      // Bundled refund request — fired as its own separate call after the
      // main action has already succeeded. Best-effort: if this one fails,
      // the main state change still stands (nothing to roll back, and
      // rolling back a reject/cancel that's already committed would be
      // wrong anyway) — the secretary can always raise it again afterwards
      // via the standalone "Refund…" action.
      if ((action === 'reject' || action === 'cancel') && wantsRefund) {
        try {
          await api(
            `/api/secretary/entries/${event.CalendarID}/submissions/${submission.id}/refund-request`,
            { method: 'POST', body: JSON.stringify({ amount: parseFloat(refundAmount), note: refundNote }) },
            sessionToken
          );
        } catch (refundErr) {
          console.error('Refund request failed after a successful ' + action + ':', refundErr);
        }
      }

      setConfirming(null);
      onActioned();
      onClose();
    } catch (err) {
      if (err.status === 409 && err.body?.conflict === 'race_number') {
        setConflict({ machine_index: err.body.machine_index, race_number: err.body.race_number });
        setConfirming(null);
        setSubmitError(err.message);
      } else {
        setConfirming(null);
        setSubmitError(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Picking an option from the "Decide" menu (or the pill shortcut below)
  // reveals that action's composer in this same modal — reject's default
  // email is seeded here using whatever reason has already been typed.
  // Reject/cancel also default the "request a refund" checkbox to checked,
  // with the amount defaulted to this submission's own fee total (never the
  // raw payment_amount — see the review-fetch comment above for why that's
  // wrong for a coverage-group payer/covered entrant) — except for a cheque
  // payment, which is normally still a physical, un-banked instrument at
  // this point: nothing has moved, so there's nothing to refund, just a
  // cheque to tear up (see the instruction shown instead, below). Leaves
  // the checkbox available either way, for the rare case a cheque was
  // already banked before this decision.
  const pickAction = (action) => {
    setMenuOpen(false);
    if (action === 'reject') {
      setEmailSubject(DEFAULT_REJECTION_SUBJECT);
      setEmailBody(defaultRejectionEmailBody(submission, event, isPending, rejectReason));
    }
    if (action === 'reject' || action === 'cancel') {
      const isCheque = submission.payment_method === 'cheque';
      setWantsRefund(!isCheque);
      const fee = review?.payment_preview?.total_fee;
      setRefundAmount(fee != null ? String(fee) : (submission.payment_amount != null ? String(submission.payment_amount) : ''));
      setRefundNote(action === 'reject' ? 'Submission rejected — refund owed.' : 'Entry cancelled — refund owed.');
    }
    setChosenAction(action);
  };

  // Opened directly from a status pill's own dropdown (see DecidePill in
  // SecretaryPortal.jsx) rather than this dialog's internal "Decide" menu —
  // lands straight on that action's composer once loading settles, same as
  // if the secretary had clicked it here themselves. Fires once only
  // (autoJumpedRef), so backing out via "Back" doesn't re-trigger it — the
  // internal Decide menu is still there afterwards if they want something else.
  const autoJumpedRef = useRef(false);
  useEffect(() => {
    if (initialAction && !autoJumpedRef.current && !reviewLoading && !reviewError) {
      autoJumpedRef.current = true;
      pickAction(initialAction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAction, reviewLoading, reviewError]);

  const raceNumbersSummary = Object.values(assignments).filter(Boolean).join(', ') || '—';

  // The name the new entry rows will carry: the chosen rider's spelling, or
  // the known rider's, else what was typed.
  const entryRiderName = riderChoice && riderChoice !== 'new'
    ? `${riderChoice.first_name} ${riderChoice.last_name}`
    : review?.rider?.status === 'known' && review.rider.rider
      ? `${review.rider.rider.first_name} ${review.rider.rider.last_name}`
      : riderName(submission);

  const confirmCopy = confirming === 'hold'
    ? `This will create entry #${raceNumbersSummary} for ${entryRiderName}, held for committee review` +
      (isReceived && bacsStatus === 'unconfirmed' ? ' or BACS payment confirmation' : '') + `. ` +
      `No email will be sent yet. This cannot be undone from here — if anything's wrong, the rider will need to resubmit.`
    : confirming === 'reject'
      ? `This will reject ${riderName(submission)}'s submission` +
        (isPending ? `, delete the held entry #${raceNumbersSummary}, and free that race number` : '') +
        `, and send a rejection email immediately` +
        (wantsRefund ? `, and flag a ${fmtCurrency(parseFloat(refundAmount))} refund as needed` : '') +
        `. This cannot be undone from here.`
      : confirming === 'cancel'
        ? `This will cancel entry #${raceNumbersSummary} for ${riderName(submission)} and free the race number` +
          (wantsRefund ? `, and flag a ${fmtCurrency(parseFloat(refundAmount))} refund as needed` : '') +
          `. No email will be sent. This cannot be undone from here.`
        : isReceived
          ? `This will create entry #${raceNumbersSummary} for ${entryRiderName} and send an acceptance email ` +
            `immediately. This cannot be undone from here — if anything's wrong, the rider will need to resubmit.`
          : `This will lock in entry #${raceNumbersSummary} for ${riderName(submission)} and send an acceptance email ` +
            `immediately. This cannot be undone from here.`;

  return (
    <div className="sec-overlay">
      <div className="sec-modal" style={{ maxWidth: '680px', maxHeight: '85vh', overflowY: 'auto' }}>
        <div className="sec-modal-header">
          <h3>Decide Entry — {riderName(submission)}</h3>
          <button className="sec-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="sec-modal-body">
          {reviewLoading ? (
            <Spinner />
          ) : reviewError ? (
            <div className="error-message">
              <p>⚠️ {reviewError}</p>
              <button onClick={load}>Retry</button>
            </div>
          ) : (
            <>
              {/* Coverage banner is data from the same /review fetch that
                  already runs for every status (received/pending/accepted)
                  — shown here unconditionally so an already-decided entry's
                  coverage code/group is still visible, not just pre-decision
                  ones. Previously only rendered in the pre-decision branch
                  below, so an accepted coverage-group payer's own overpayment
                  was invisible once accepted. */}
              {review.coverage?.role === 'payer' && (
                <div className="info-banner" style={{ background: '#e8f4fd', borderColor: '#1b3d6d', marginBottom: '1rem' }}>
                  <strong>Coverage code:</strong> {formatCoverageCode(review.coverage.coverage_code) || '—'}
                  {review.coverage.covered_entrants.length === 0 ? (
                    <p style={{ marginTop: '0.4rem', marginBottom: 0 }}>
                      Not used yet — share this code with anyone whose entry fee this payment also covers.
                    </p>
                  ) : (
                    <>
                      <p style={{ marginTop: '0.4rem', marginBottom: '0.4rem' }}>
                        Covering {review.coverage.covered_entrants.length} other{' '}
                        {review.coverage.covered_entrants.length === 1 ? 'entrant' : 'entrants'}:
                      </p>
                      <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                        {review.coverage.covered_entrants.map(c => (
                          <li key={c.submission_id}>
                            {c.rider_name} — {c.status}
                            {c.total_shortfall > 0 && (
                              <span style={{ color: 'var(--danger-color)' }}> (shortfall {fmtCurrency(c.total_shortfall)})</span>
                            )}
                          </li>
                        ))}
                      </ul>
                      <p style={{ marginTop: '0.4rem', marginBottom: 0 }}>
                        Group total fee: {fmtCurrency(review.coverage.group_total_fee)}
                        {review.coverage.group_surplus > 0 && <> · surplus {fmtCurrency(review.coverage.group_surplus)}</>}
                        {review.coverage.group_total_shortfall > 0 && <> · shortfall {fmtCurrency(review.coverage.group_total_shortfall)}</>}
                      </p>
                    </>
                  )}
                </div>
              )}

              {review.coverage?.role === 'covered' && (
                <div className="info-banner" style={{ background: '#e8f4fd', borderColor: '#1b3d6d', marginBottom: '1rem' }}>
                  Covered by <strong>{review.coverage.payer_rider_name}</strong>&apos;s BACS payment
                  (submission #{review.coverage.payer_submission_id}, currently {review.coverage.payer_status}).
                </div>
              )}

              {(isPending || isAccepted) ? (
                /* Already decided at hold/accept-time — race number
                   assignment, ACU/name-mismatch checks all happened then.
                   Just a summary; an accepted entry has no email composer
                   at all, since Cancel is silent. Payment/"X of Y" is
                   rendered below, unconditionally, same as the pre-decision
                   branch. */
                <div style={{ background: 'var(--bg-color)', borderRadius: '8px', padding: '1rem', marginBottom: '1.25rem' }}>
                  <p>
                    <strong>{isAccepted ? 'Entry:' : 'Held at:'}</strong> race number{machines.length > 1 ? 's' : ''}{' '}
                    {machines.map((m, i) => assignments[i] || '—').join(', ')}
                  </p>
                </div>
              ) : (
                <>
                  <RiderSection
                    riderReview={review.rider}
                    submission={submission}
                    choice={riderChoice}
                    onChoose={setRiderChoice}
                  />
                  <GiagWarning licence={review.licence} riderReview={review.rider} choice={riderChoice} />

                  {review.acu_match && (
                    <div className="info-banner" style={{ background: '#fff3e0', borderColor: '#fb6107', marginBottom: '1rem' }}>
                      <strong>ACU number match:</strong> this rider&apos;s ACU licence number matches an existing entry
                      for <strong>{review.acu_match.rider_first} {review.acu_match.rider_last}</strong> (race #{review.acu_match.race_number},
                      {' '}{review.acu_match.event_hill} {review.acu_match.event_date}) — check this isn&apos;t the same person
                      under a slightly different name before continuing.
                    </div>
                  )}

                  {machines.map((m, i) => {
                    const preview = review.machines.find(mp => mp.machine_index === i);
                    const history = machineHistory[i] || [];
                    const isConflicted = conflict?.machine_index === i;
                    return (
                      <div key={i} style={{ marginBottom: '1.25rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border-color)' }}>
                        <p className="form-section-label">
                          Machine {machines.length > 1 ? i + 1 : ''} — {[m.make, m.model].filter(Boolean).join(' ') || 'Unlisted machine'}
                        </p>

                        {preview?.name_mismatch && (
                          <div className="info-banner" style={{ background: '#fdecea', borderColor: '#c62828', marginBottom: '0.75rem' }}>
                            <strong>Name mismatch:</strong> the submission gives the rider as{' '}
                            <strong>{riderName(submission)}</strong>, but race number history shows{' '}
                            <strong>{preview.name_mismatch.historical_name}</strong> last used this number — confirm
                            whether this is the same rider before continuing.
                          </div>
                        )}

                        <div className="form-group" style={{ maxWidth: '160px' }}>
                          <label>Race Number</label>
                          <input
                            type="number"
                            min="1"
                            value={assignments[i] ?? ''}
                            onChange={e => setAssignment(i, e.target.value)}
                          />
                          {isConflicted && (
                            <p style={{ color: 'var(--danger-color)', fontSize: '0.85rem', marginTop: '0.3rem' }}>
                              Race number {conflict.race_number} is already in use — choose a different number.
                            </p>
                          )}
                        </div>

                        <p style={{ marginTop: '0.5rem' }}>
                          <strong>Predicted fee:</strong> {preview?.predicted_fee != null ? fmtCurrency(preview.predicted_fee) : 'not configured'}
                          {i === 0 && review.licence && (
                            <span style={{ color: 'var(--text-light)' }}>
                              {review.licence.option === 'giag'
                                ? ` (Give It A Go ${fmtCurrency(review.licence.fee)}, in place of the entry fee)`
                                : ` (includes ${[
                                    review.licence.fee != null && `day licence ${fmtCurrency(review.licence.fee)}`,
                                    review.licence.membership_fee != null && `day membership ${fmtCurrency(review.licence.membership_fee)}`,
                                  ].filter(Boolean).join(' and ')})`}
                            </span>
                          )}
                        </p>

                        {history.length > 0 && (
                          <div className="history-results">
                            <p>{history.length} previous entry/entries found for this number:</p>
                            {history.map((h, hi) => (
                              <div key={hi} className="history-card" style={{ cursor: 'default' }}>
                                <div className="history-card-name">
                                  {[h.rider_first, h.rider_last].filter(Boolean).join(' ') || 'Unknown Rider'}
                                </div>
                                <div className="history-card-bike">
                                  {[h.manufacturer, h.model, h.cc ? `${h.cc}cc` : null].filter(Boolean).join(' ')}
                                  {' · last seen '}{fmtDateTime(h.event_date)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              <div style={{ background: 'var(--bg-color)', borderRadius: '8px', padding: '1rem', marginBottom: '1.25rem' }}>
                <p style={{ marginBottom: '0.3rem' }}>
                  {/* Always "x of y", even when they're equal (no coverage
                      code, exact payment) — a conditional format here just
                      means re-learning which layout you're looking at. Shown
                      unconditionally (not just pre-decision) — same reasoning
                      as the coverage banner above. */}
                  <strong>Payment:</strong> {fmtCurrency(review.payment_preview?.total_fee)} of {fmtCurrency(submission.payment_amount)} stated ({submission.payment_method?.toUpperCase()})
                </p>
                {review.payment_preview?.surplus > 0 && !(review.coverage?.role === 'payer' && review.coverage.covered_entrants.length > 0) && (
                  <p style={{ color: '#fb6107' }}>
                    ⚠️ Surplus of {fmtCurrency(review.payment_preview.surplus)} — does not match this submission&apos;s own
                    fees.{review.payment_race_numbers ? ` Payment also stated to cover race numbers: ${review.payment_race_numbers} — reconcile manually.` : ' Reconcile manually.'}
                  </p>
                )}
                {review.payment_preview?.total_shortfall > 0 && (
                  <p style={{ color: 'var(--danger-color)' }}>
                    ⚠️ Shortfall of {fmtCurrency(review.payment_preview.total_shortfall)} still owed.
                  </p>
                )}
              </div>

              {/* Only shown once an action has actually been picked (chosenAction) —
                  reject's reason field, the bundled refund checkbox (reject/cancel),
                  then the shared accept/reject email composer. Hold has nothing extra. */}
              {chosenAction === 'reject' && (
                <div className="form-group">
                  <label>Reason (kept on record, and updates the email below)</label>
                  <textarea
                    rows={2}
                    value={rejectReason}
                    onChange={e => {
                      const val = e.target.value;
                      setRejectReason(val);
                      setEmailBody(defaultRejectionEmailBody(submission, event, isPending, val));
                    }}
                    style={{ resize: 'vertical', fontSize: '0.95rem' }}
                  />
                </div>
              )}

              {(chosenAction === 'reject' || chosenAction === 'cancel') && submission.payment_method === 'cheque' && (
                <div className="info-banner" style={{ background: '#fff3e0', borderColor: '#fb6107', marginBottom: '1rem' }}>
                  <strong>Paid by cheque:</strong> unless it&apos;s already been banked, nothing has actually moved yet —
                  just destroy this rider&apos;s cheque, no refund needs tracking. Tick the box below only if it turns
                  out this one was already banked.
                </div>
              )}

              {(chosenAction === 'reject' || chosenAction === 'cancel') && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
                    <input type="checkbox" checked={wantsRefund} onChange={e => setWantsRefund(e.target.checked)} />
                    {submission.payment_method === 'cheque' ? 'This cheque was already banked — request a refund' : 'Set the refund amount now'}
                  </label>
                  {wantsRefund ? (
                    <div style={{ marginTop: '0.5rem' }}>
                      <div className="form-group">
                        <label>Amount (£)</label>
                        <input type="number" step="0.01" min="0" value={refundAmount}
                               onChange={e => setRefundAmount(e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label>Note</label>
                        <textarea rows={2} value={refundNote} onChange={e => setRefundNote(e.target.value)}
                                  style={{ resize: 'vertical', fontSize: '0.95rem' }} />
                      </div>
                    </div>
                  ) : submission.payment_method !== 'cheque' && (
                    <p className="muted-note" style={{ marginTop: '0.3rem' }}>
                      If there&apos;s money on record for this entry, a refund is flagged as needed either way — leaving
                      this unticked just means it&apos;s flagged for the full amount stated, for the financial secretary
                      to check in Refund Requests, rather than the (usually smaller) amount you&apos;d set here now.
                    </p>
                  )}
                </div>
              )}

              {chosenAction === 'accept' && bacsStatus === 'unconfirmed' && (
                <div className="info-banner" style={{ background: '#ffebee', borderColor: '#c0392b', marginBottom: '1rem' }}>
                  <strong>BACS payment not yet confirmed:</strong>{' '}
                  the financial secretary hasn’t confirmed this bank transfer against the statement yet, so this
                  entry can’t be accepted until they do.
                  {isReceived && ' You can hold it for review meanwhile; that keeps the race number.'}
                </div>
              )}

              {chosenAction === 'accept' && bacsStatus === 'underpaid' && (
                <div className="info-banner" style={{ background: '#fff3e0', borderColor: '#fb6107', marginBottom: '1rem' }}>
                  <strong>Confirmed BACS amount is short:</strong> financial confirmed £{review.bacs_confirmation.actual_amount.toFixed(2)} but
                  £{review.bacs_confirmation.owed.toFixed(2)} is owed. You can still accept this entry, but the
                  shortfall will need chasing separately.
                </div>
              )}

              {(chosenAction === 'accept' || chosenAction === 'reject') && (
                <>
                  <p className="form-section-label">{chosenAction === 'reject' ? 'Rejection Email' : 'Acceptance Email'}</p>
                  {renderComposer()}
                </>
              )}

              {submitError && !conflict && (
                <div className="error-message" style={{ marginTop: '0.5rem' }}>
                  <p>⚠️ {submitError}</p>
                  <button onClick={() => setSubmitError(null)}>Dismiss</button>
                </div>
              )}
            </>
          )}
        </div>

        {!reviewLoading && !reviewError && (
          <div className="sec-modal-footer">
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
            {chosenAction ? (
              <button
                className={decideOptions.find(o => o.action === chosenAction)?.danger ? 'btn-delete' : 'btn-save'}
                disabled={(wantsRefund && (!refundAmount || !refundNote.trim())) || (chosenAction === 'accept' && bacsBlocksAccept)
                  || ((chosenAction === 'accept' || chosenAction === 'hold') && riderUnsettled)}
                onClick={() => setConfirming(chosenAction)}
              >
                {actionLabel}
              </button>
            ) : decideOptions.length === 1 ? (
              <button
                className={decideOptions[0].danger ? 'btn-delete' : 'btn-save'}
                onClick={() => pickAction(decideOptions[0].action)}
              >
                {decideOptions[0].label}
              </button>
            ) : (
              <div className="action-menu-wrap" ref={menuRef}>
                <button type="button" className="btn-save" onClick={() => setMenuOpen(o => !o)}>
                  Decide ▾
                </button>
                {menuOpen && (
                  <div className="action-menu">
                    {decideOptions.map(opt => (
                      <button
                        key={opt.action}
                        type="button"
                        className={`action-menu-item${opt.danger ? ' action-menu-item-danger' : ''}`}
                        style={opt.disabled ? { opacity: 0.5 } : undefined}
                        title={opt.disabled ? 'BACS payment not yet confirmed — click for details' : undefined}
                        onClick={() => pickAction(opt.action)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {confirming && (
        <div className="confirm-overlay">
          <div className="confirm-card">
            <h3>
              {confirming === 'hold' ? 'Hold for Review?'
                : confirming === 'reject' ? 'Confirm Rejection'
                : confirming === 'cancel' ? 'Confirm Cancellation'
                : 'Confirm Acceptance'}
            </h3>
            <p>{confirmCopy}</p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setConfirming(null)} disabled={submitting}>Cancel</button>
              <button className="btn-confirm-delete" onClick={() => doSubmit(confirming)} disabled={submitting}>
                {submitting ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
